import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  CircularProgress,
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  Autocomplete,
  Grid,
  Paper,
  Stack,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import PropTypes from "prop-types";

export default function ExpenseBreakdownDialog({
  open,
  onClose,
  startDate,
  endDate,
  selectedLedger,
  breakdownData = [],
  loading = false,
  error = null,
  type = "expense",
  selectedDsaId,
}) {
  const [filteredData, setFilteredData] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [columnOptions, setColumnOptions] = useState([]);
  const gridRef = useRef();

  const formatIndianCurrency = (value) => {
    if (value == null || value === "") return "";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const numericCellStyle = (params) => ({
    fontWeight: params.node?.rowPinned === "bottom" ? "bold" : "500",
    textAlign: "right",
    whiteSpace: "normal",
    lineHeight: "1.2",
    paddingTop: "2px",
    paddingBottom: "2px",
    ...(params.node?.rowPinned === "bottom" ? { backgroundColor: "#f5f5f5" } : {}),
  });

  const columnDefs = useMemo(() => {
    if (type === "income" || type === "misIncome") {
      return [
        {
          headerName: "Business Type",
          field: "businessType",
          sortable: true,
          filter: true,
        },
        {
          headerName: "Bank Name",
          field: "bankerName",
          sortable: true,
          filter: true,
        },
        {
          headerName: "Application No",
          field: "applicationNumber",
          sortable: true,
          filter: true,
        },
        {
          headerName: "Customer Name",
          field: "customerName",
          sortable: true,
          filter: true,
        },
        {
          headerName: "Disbursed Amt",
          field: "disbursedAmount",
          sortable: true,
          filter: true,
          type: "numericColumn",
          aggFunc: "sum",
          valueFormatter: (params) => formatIndianCurrency(params.value),
          cellStyle: numericCellStyle,
        },
        {
          headerName: "Amount",
          field: "amount",
          sortable: true,
          filter: true,
          type: "numericColumn",
          aggFunc: "sum",
          valueFormatter: (params) => formatIndianCurrency(params.value),
          cellStyle: numericCellStyle,
        },
        {
          headerName: "DMA Payout",
          field: "dmaPayout",
          sortable: true,
          filter: true,
          type: "numericColumn",
          aggFunc: "sum",
          valueFormatter: (params) => formatIndianCurrency(params.value),
          cellStyle: numericCellStyle,
        },
        {
          headerName: "Provider Name",
          field: "providerName",
          sortable: true,
          filter: true,
        },
        {
          headerName: "DSA Name",
          field: "dsaName",
          sortable: true,
          filter: true,
          hide: selectedDsaId === 0,
          valueGetter: (params) => params.data?.dsaName || "",
        },
      ];
    }

    return [
      {
        headerName: "Date",
        field: "date",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Voucher ID",
        field: "voucherDisplayID",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Particulars",
        field: "particulars",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Amount",
        field: "amount",
        sortable: true,
        filter: true,
        type: "numericColumn",
        aggFunc: "sum",
        valueFormatter: (params) => formatIndianCurrency(params.value),
        cellStyle: (params) => ({
          fontWeight: "bold",
          textAlign: "right",
          whiteSpace: "normal",
          lineHeight: "1.2",
          paddingTop: "2px",
          paddingBottom: "2px",
        }),
      },
      {
        headerName: "Remarks",
        field: "remarks",
        sortable: true,
        filter: true,
      },
    ];
  }, [type, selectedDsaId]);

  useEffect(() => {
    const options = columnDefs
      .filter((col) => col.field)
      .map((col) => ({
        label: col.headerName,
        value: col.field,
        field: col.field,
        type: col.type || "string",
      }));
    setColumnOptions(options);
    if (options.length > 0 && !searchColumn) {
      setSearchColumn(options[0]);
    }
  }, [columnDefs]);

  useEffect(() => {
    setFilteredData(breakdownData || []);
  }, [breakdownData]);

  useEffect(() => {
    if (!searchText.trim() || !searchColumn) {
      setFilteredData(breakdownData || []);
      return;
    }
    const lowerSearchText = searchText.toLowerCase();
    const filtered = breakdownData.filter((row) => {
      const fieldValue = row[searchColumn.field];
      if (fieldValue == null) return false;
      if (searchColumn.type === "numericColumn" || typeof fieldValue === "number") {
        return fieldValue.toString().includes(searchText);
      }
      return fieldValue.toString().toLowerCase().includes(lowerSearchText);
    });
    setFilteredData(filtered);
  }, [breakdownData, searchColumn, searchText]);

  const calculateFooterTotals = useCallback((data) => {
    if (!data || data.length === 0) {
      return { disbursedAmount: 0, amount: 0, dmaPayout: 0, count: 0 };
    }
    return {
      disbursedAmount: data.reduce((sum, row) => sum + (parseFloat(row.disbursedAmount) || 0), 0),
      amount: data.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0),
      dmaPayout: data.reduce((sum, row) => sum + (parseFloat(row.dmaPayout) || 0), 0),
      count: data.length,
    };
  }, []);

  useEffect(() => {
    const totals = calculateFooterTotals(filteredData);
    if (type === "income" || type === "misIncome") {
      setPinnedBottomRowData([
        {
          businessType: "Total",
          disbursedAmount: totals.disbursedAmount,
          amount: totals.amount,
          dmaPayout: totals.dmaPayout,
        },
      ]);
    } else {
      setPinnedBottomRowData([
        {
          particulars: "Total",
          amount: totals.amount,
          remarks: `${totals.count} entries`,
        },
      ]);
    }
  }, [filteredData, searchText, type, calculateFooterTotals]);

  const handleSearchColumnChange = (event, newValue) => {
    setSearchColumn(newValue);
    if (!newValue) setSearchText("");
  };

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchColumn(columnOptions[0] || null);
    setSearchText("");
  };

  const handleDownloadExcel = () => {
    if (!filteredData || filteredData.length === 0) return;
    const allData = [...filteredData, ...pinnedBottomRowData];

    let exportData;
    if (type === "income" || type === "misIncome") {
      exportData = allData.map((row) => {
        const mappedRow = {
          "Business Type": row.businessType || "",
          "Application Number": row.applicationNumber || "",
          "Customer Name": row.customerName || "",
          "Disbursed Amount": row.disbursedAmount || 0,
          Amount: row.amount || 0,
          "DMA Payout": row.dmaPayout || 0,
          "Provider Name": row.providerName || "",
        };
        if (selectedDsaId !== 0) mappedRow["DSA Name"] = row.dsaName || "";
        return mappedRow;
      });
    } else {
      exportData = allData.map((row) => ({
        Date: row.date || "",
        "Voucher ID": row.voucherDisplayID || "",
        Particulars: row.particulars || "",
        Amount: row.amount || 0,
        Remarks: row.remarks || "",
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    const breakdownLabel = type === "income" ? "Income Breakdown" : "Expense Breakdown";
    XLSX.utils.book_append_sheet(workbook, worksheet, breakdownLabel);
    const maxWidths = [];
    exportData.forEach((row) => {
      Object.values(row).forEach((val, i) => {
        const len = val ? val.toString().length : 10;
        maxWidths[i] = Math.max(maxWidths[i] || 10, len);
      });
    });
    worksheet["!cols"] = maxWidths.map((w) => ({ wch: w + 2 }));
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `${breakdownLabel.replace(" ", "_")}_${selectedLedger}_${startDate}_to_${endDate}.xlsx`
    );
  };

  if (!open) return null;

  const pageTitle =
    type === "income" || type === "misIncome" ? "Income Breakdown" : "Expense Breakdown";

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#f4f6f8",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Top Bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          flexShrink: 0,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Back
        </Button>

        <Typography variant="h6" fontWeight={700} color="#1F2937">
          {pageTitle} — {selectedLedger}
        </Typography>

        <Button
          variant="contained"
          onClick={handleDownloadExcel}
          disabled={filteredData.length === 0}
          sx={{
            textTransform: "none",
            bgcolor: "#f57c00",
            "&:hover": { bgcolor: "#e65100" },
            color: "#fff",
            borderRadius: 2,
          }}
        >
          Download Excel
        </Button>
      </Box>

      {/* ── Filter / Search Section ── */}
      <Box sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 2,
            backgroundColor: "#fff",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#333">
              🔍 Search
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={handleClearSearch}
              disabled={!searchText}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              Clear Search
            </Button>
          </Stack>

          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={columnOptions}
                getOptionLabel={(option) => option.label}
                value={searchColumn}
                onChange={handleSearchColumnChange}
                disableClearable={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Column"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Search Text"
                variant="outlined"
                size="small"
                value={searchText}
                onChange={handleSearchTextChange}
                placeholder={
                  searchColumn ? `Search in ${searchColumn.label}...` : "Select a column first"
                }
                disabled={!searchColumn}
              />
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.2, display: "block" }}>
            {searchText && searchColumn ? (
              <>
                Showing <strong>{filteredData.length}</strong> of{" "}
                <strong>{breakdownData.length}</strong> rows &nbsp;|&nbsp;
                <span style={{ color: "#1976d2" }}>
                  {searchColumn.label} = &quot;{searchText}&quot;
                </span>
              </>
            ) : (
              <>
                Total <strong>{breakdownData.length}</strong> rows
              </>
            )}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            px: 2,
            py: 1,
            bgcolor: "#fff",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing data from <strong>{startDate}</strong> to <strong>{endDate}</strong>
          </Typography>
        </Paper>
      </Box>

      {/* ── AG Grid — minHeight:0 chain fixes sticky header ── */}
      <Box
        sx={{
          flex: 1,
          px: 3,
          pb: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : filteredData.length === 0 ? (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Typography color="text.secondary">
              {searchText && searchColumn
                ? "No matching records found"
                : "No breakdown data available"}
            </Typography>
          </Box>
        ) : (
          <div
            className="ag-theme-alpine"
            style={{
              flex: 1,
              width: "100%",
              height: "100%",
              minHeight: 0,
            }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              pinnedBottomRowData={pinnedBottomRowData}
              defaultColDef={{
                resizable: true,
                flex: 1,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                  whiteSpace: "normal",
                  lineHeight: "1.2",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                },
              }}
              onGridReady={(params) => {
                params.api.sizeColumnsToFit();
              }}
              onFirstDataRendered={(params) => {
                params.api.sizeColumnsToFit();
              }}
              getRowStyle={(params) => {
                if (params.node.rowPinned === "bottom") {
                  return { fontWeight: "bold", backgroundColor: "#f5f5f5" };
                }
                return null;
              }}
              pagination
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
              headerHeight={48}
              suppressAggFuncInHeader
              suppressMovableColumns
              suppressColumnVirtualisation
              animateRows
            />
          </div>
        )}
      </Box>
    </Box>
  );
}

ExpenseBreakdownDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  selectedLedger: PropTypes.string.isRequired,
  breakdownData: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  type: PropTypes.string,
  selectedDsaId: PropTypes.number,
};
